import { useEffect, useState } from 'react';
import { Bell, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface Alert {
  id: string; type: string; severity: 'critical' | 'warning' | 'info';
  title: string; body: string; resolved: boolean; created_at: string;
}

const SEVERITY_STYLE = {
  critical: { color: '#f87171', bg: 'rgba(239,68,68,0.1)',   icon: XCircle,       border: 'rgba(239,68,68,0.3)' },
  warning:  { color: '#fbbf24', bg: 'rgba(251,191,36,0.08)', icon: AlertTriangle, border: 'rgba(251,191,36,0.3)' },
  info:     { color: '#60a5fa', bg: 'rgba(96,165,250,0.08)', icon: Bell,          border: 'rgba(96,165,250,0.2)' },
};

export function AdminAlertsPage() {
  const [alerts,   setAlerts]   = useState<Alert[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState<'open' | 'resolved'>('open');

  useEffect(() => { load(); }, [tab]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('admin_alerts')
      .select('*')
      .eq('resolved', tab === 'resolved')
      .order('created_at', { ascending: false })
      .limit(100);
    setAlerts((data ?? []) as Alert[]);
    setLoading(false);
  }

  async function resolve(id: string) {
    await supabase.from('admin_alerts').update({ resolved: true, resolved_at: new Date().toISOString() }).eq('id', id);
    setAlerts(prev => prev.filter(a => a.id !== id));
  }

  async function resolveAll() {
    if (!window.confirm('Resolver todos os alertas abertos?')) return;
    const ids = alerts.map(a => a.id);
    await supabase.from('admin_alerts').update({ resolved: true, resolved_at: new Date().toISOString() }).in('id', ids);
    setAlerts([]);
  }

  const open = alerts.filter(a => !a.resolved).length;

  return (
    <div>
      <AdminPageHeader title="Alertas do Sistema" objective="Notificações automáticas sobre situações que requerem atenção: erros de integração, limites críticos, falhas de pagamento. Resolva alertas críticos imediatamente, podem indicar perda de receita ou problemas de acesso para usuários." />

      {/* Severity legend */}
      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        🔴 Crítico = ação imediata | 🟡 Aviso = atenção necessária | 🔵 Info = informativo
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)', margin: 0 }}>Alertas</h1>
          {open > 0 && tab === 'open' && (
            <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(239,68,68,0.15)', color: '#f87171', fontWeight: 700 }}>
              {open} abertos
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {tab === 'open' && alerts.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={resolveAll}>✓ Resolver todos</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem' }}>
        <button onClick={() => setTab('open')} className={tab === 'open' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          Abertos
        </button>
        <button onClick={() => setTab('resolved')} className={tab === 'resolved' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          Resolvidos
        </button>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '200px' }} />
      ) : alerts.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <CheckCircle size={40} color="#4ade80" style={{ marginBottom: '1rem', opacity: 0.7 }} />
          <p style={{ color: 'var(--text-primary)', fontWeight: 600, margin: '0 0 0.25rem' }}>
            {tab === 'open' ? 'Nenhum alerta aberto' : 'Nenhum alerta resolvido'}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
            {tab === 'open' ? 'O sistema está operando normalmente ✦' : ''}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {alerts.map(alert => {
            const style = SEVERITY_STYLE[alert.severity] ?? SEVERITY_STYLE.info;
            const Icon  = style.icon;
            return (
              <div key={alert.id} className="card" style={{ border: `1px solid ${style.border}`, backgroundColor: style.bg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Icon size={18} color={style.color} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: style.color, fontSize: 'var(--text-label)' }}>{alert.title}</span>
                      <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {alert.severity}
                      </span>
                    </div>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{alert.body}</p>
                    <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                      {new Date(alert.created_at).toLocaleString('pt-BR')} · Tipo: {alert.type}
                    </p>
                  </div>
                  {tab === 'open' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => resolve(alert.id)} style={{ flexShrink: 0 }}>
                      ✓ Resolver
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
